import { Container, Typography, Box, CircularProgress } from "@mui/material";
import React, { useContext, useMemo, useCallback, useState } from "react";
import { ChartFooter } from "../styles/common.styled"; 
import { useLocation, useNavigate } from "react-router-dom";
import { CONSTELLATION_UTIL_MAPPING } from "../../config";
import { CompareGridContextProps, PlanProps } from "../../types";
import { pushToDataLayer, KH_CODES, KH_CODES_MAP, getBestRate, getBestValue } from "../data";
import { CompareGridContext } from "../../context-manager";
import { getCurrentPromoCode } from "../../utils/promoCodeUtils";
import { CompareChart } from "../CompareChart";
import { KHPlanModal } from "./KHPlanModal";   
import { Grid, Paper } from "@mui/material";
export function KhCompareChart() {
    const location = useLocation();
    const navigate = useNavigate();
    const defaultPromoCode = getCurrentPromoCode(location.pathname);
    const [showKHModal, setShowKHModal] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);    
    const {products, acPlans,gridData,setOneRepPlanList, setShowModal,showModal,promoCode, zipcode,utilityCode,state,
    } = useContext(CompareGridContext) as CompareGridContextProps;
    // Check if this is a KH page based on promo code
    const isKhPage = useMemo(() => {
        return KH_CODES.includes(promoCode || "");
    }, [promoCode]);
    // Filter products based on KH page logic
    const filteredProducts = useMemo(() => {
        if (!products || products.length === 0) return [];        
        const BestRate = getBestRate(promoCode);
        const BestValue = getBestValue(promoCode);        
        if (promoCode === defaultPromoCode) {
            return products.filter((item) => BestRate.indexOf(Number(item.plan_id)) > -1 || BestValue.indexOf(Number(item.plan_id)) > -1);
        } else {
            if (isKhPage) {
                return products.filter((item: PlanProps) => BestRate.indexOf(Number(item.plan_id)) > -1 ||  BestValue.indexOf(Number(item.plan_id)) > -1);
            } else {
                return products.filter((item: PlanProps) => (BestRate.indexOf(Number(item.plan_id)) > -1 || BestValue.indexOf(Number(item.plan_id)) > -1) &&  Number(item.contract_term) === 12 );
            }
        }
    }, [products, promoCode, isKhPage, defaultPromoCode]);
    // Transform gridData into chartData format for CompareChart
    const chartData = useMemo(() => {
        if (!gridData || gridData.length === 0) return [];        
        // Sort gridData by value (rate) in ascending order
        const sortedData = [...gridData].sort((a, b) => a.value - b.value);        
        // Transform to chart format with plan names from acPlans
        return sortedData.map((item) => {
            const plan = acPlans?.find(p => p.provider_id === item.id);
            let planName = plan?.plan_name || item.label;            
            // Modify plan name: "12 Month Usage" to "Fixed Rate + Bill Credit"
            if (planName === "12 Month Usage") { planName = "Fixed Rate + Bill Credit"; }            
            return {
                id: item.id,
                value: item.value,
                label: item.label,
                planName: planName,
            };
        });
    }, [gridData, acPlans]);
    // Push data layer for analytics
    const pushDataLayer = useCallback((product: any, rate: number) => {
        const { plan_name, plan_id, contract_term } = product || {};
        if (!plan_name) return;
        const mappedUtilityCode = utilityCode ? CONSTELLATION_UTIL_MAPPING[utilityCode as keyof typeof CONSTELLATION_UTIL_MAPPING] || utilityCode : "";
        pushToDataLayer({
            event: "sign_up_start",
            electricity_plan_name: plan_name,
            electricity_plan_rate: rate?.toFixed(1),
            page_path: location.pathname,
            plan_term: contract_term,
            provider_code: mappedUtilityCode,
            user_region: state || "TX",
            zipcode: zipcode || "",
            electricity_rate_plan_id: plan_id,     
            campaign_source: "",
        });
    }, [utilityCode, state, zipcode, location.pathname]);
    // Create repMap for rate lookup (same as kh_grid)
    const repMap = useMemo(() => {
        const m: Record<string, number> = {};
        (gridData || []).forEach((r: any) => {
            m[String(r.id)] = Number(r.value);
        });
        return m;
    }, [gridData]);
    // Handle sign up for Constellation plans 
    const handleSignUp = useCallback((plan: any) => {
        let product;  
        if (isKhPage) {
            product =
                plan.plan_name === "Renewable Plan*"
                    ? products.find((p) => p.plan_name.includes("Usage Bill Credit"))
                    : products.find((p) =>
                        p.plan_name.toLowerCase().includes(plan.plan_name.toLowerCase()) || p.plan_name.toLowerCase().includes("saver's choice 12")
                    );
        } else {
            product = products.find((p) =>
                p.plan_name.toLowerCase().includes(plan.plan_name.toLowerCase()) || p.plan_name.toLowerCase().includes("saver's choice 12")
            );
        }      
        if (!product) return;  
        const rate = repMap[plan.provider_id];
        localStorage.setItem("selectProduct", JSON.stringify(product));
        pushDataLayer(product, rate);  
        if (!showModal || isKhPage) {
            navigate(`/v2/enrollment/step1${location.search}`);
        } else {
            setOneRepPlanList(products);
            setShowModal(true);
        }
    }, [isKhPage, showModal, products, navigate, location.search, setOneRepPlanList, setShowModal, repMap, pushDataLayer]);

    // Wrapper function to convert ID to plan object for CompareChart
    const handleSignUpWrapper = useCallback((id: string) => {
        const plan = acPlans?.find(p => p.provider_id === id);
        if (plan) {
            handleSignUp(plan);
        }
    }, [acPlans, handleSignUp]);
    // Handle show plan details
    const handleShowPlan = useCallback((id: string) => {
        setSelectedPlanId(id);
        setShowKHModal(true);
    }, []);

    // Handle close KH modal
    const handleCloseKHModal = useCallback(() => {
        setShowKHModal(false);
        setSelectedPlanId(null);
    }, []);
    // Calculate energy savings following KH logic
    const energySavings = useMemo(() => {
        if (!chartData || chartData.length < 2) return 0;
        const rateDiff = chartData[chartData.length - 1].value - chartData[0].value;
        return (rateDiff * parseFloat('1000') * parseInt('12')) / 100; // Following ac-page.tsx logic
    }, [chartData]);
    if (!chartData || chartData.length === 0) {
        return (
            <Container className="main-container">
                <Box sx={{ backgroundColor: "#fff", p: 3, textAlign: "center" }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Loading comparison data...
                    </Typography>
                </Box>
            </Container>
        );
    }    
    return (
        <Container className="main-container" sx={{ padding: "0px" }}>
            <Box sx={{ backgroundColor: "#fff" }}> 
                <p style={{ marginBottom: "15px" }}>
                    <b>Fixed Electric Supply Rate </b>   
                    <span style={{ fontSize: "18px" }}>(Based on 1000 kWh)</span>
                </p>                
                <CompareChart
                    chartData={chartData}
                    handleSignUp={handleSignUpWrapper}
                    handleShowPlan={handleShowPlan}
                />                
            </Box>
            
            {/* KH Plan Details Modal */}
            <KHPlanModal
                show={showKHModal}
                handleClose={handleCloseKHModal}
                planData={products}
                acPlans={acPlans}
                selectedPlanId={selectedPlanId}
                chartData={chartData}
                onSignUp={handleSignUpWrapper}
            />
        {/* Offer Boxes - Only show for OFFERKH2 and OFFERKH4 */}
        <ChartFooter>
        The comparisons above are to companies that we believe meet our standard
        of quality, and do not represent all plans available from us or from our
        competitors listed. Plans are subject to change based on market
        conditions and provider offerings.
      </ChartFooter>
        <Box sx={{ mt: 5 }}>
          <Grid container spacing={2} justifyContent="center">
            {/* Left Offer Box */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={1}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  height: '100%',
                  borderRadius: 1
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#e2e500',
                    color: 'text.primary',
                    px: 2,
                    py: 1,
                    borderRadius: "1px 12px 1px 12px",
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 1.875,
                    display: 'inline-block'
                  }}
                >
                  PLUS
                </Box>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1.25, fontSize: '1.8rem', lineHeight: 1.1 }} >
                  Save up to $600 in annual bill credits.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.1, fontSize: '1.2rem !important'}}
                >
                  Every month, you'll see up to $50 in credits on your bill based on your energy usage.
                </Typography>
              </Paper>
            </Grid>

            {/* Middle Offer Box */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={1}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  height: '100%',
                  borderRadius: 1
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#e2e500',
                    color: 'text.primary',
                    px: 2,
                    py: 1,
                    borderRadius: "1px 12px 1px 12px",
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 1.875,
                    display: 'inline-block'
                  }}
                >
                  PLUS
                </Box>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1.25, fontSize: '1.8rem', lineHeight: 1.1 }} >
                  Get a $150 gift card when you enroll.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.1, fontSize: '1.2rem !important'}}
                >
                  Here's a nice bonus just for switching, just for you. Spend it on anything you like.
                </Typography>
              </Paper>
            </Grid>

            {/* Right Offer Box */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={1}
                sx={{
                  p: 2.5,
                  textAlign: 'center',
                  height: '100%',
                  borderRadius: 1
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#e2e500',
                    color: 'text.primary',
                    px: 2,
                    py: 1,
                    borderRadius: "1px 12px 1px 12px",
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 1.875,
                    display: 'inline-block'
                  }}
                >
                  BONUS SAVINGS!
                </Box>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1.25, fontSize: '1.8rem', lineHeight: 1.1 }} >
                  Get $150 to cover the cancellation fee with your current provider.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        </Container>
    );
}